import { TableContainer, useTheme } from "@mui/material";
import { EkpTableProps, RowData } from "src/components/tables/ekp-table/commonTypes";
import { SenecEkpTheme } from "src/styles/ekp-theme/senecEkpTheme";
import { EkpTableDesktop } from "src/components/tables/ekp-table/EkpTableDesktop";
import { EkpTableMobile } from "src/components/tables/ekp-table/EkpTableMobile";
import { useScreenSize } from "src/hooks/useScreenSize";

/**
 * Represents the properties for the EkpTable component, excluding "filteredRows".
 */
type EkpTableBodyProps = Omit<EkpTableProps, "filteredRows">;

/**
 * EKP table
 *
 * - Based on MUI Table: https://mui.com/material-ui/react-table/
 *
 * Due to a different design goal for the EKP the BasicTable was not used.
 * On mobile the rows content behaves as follows:
 * - The title takes the full width of the row
 * - the other content is below the title, separated with a "-" and left aligned
 * - the last entry of a row is aligned to the right of the row
 */
export const EkpTable = ({
    rows = [] as RowData[],
    noDataText = "No data found",
    loading = false,
    handleRowClick,
}: EkpTableBodyProps) => {
    const theme: SenecEkpTheme = useTheme();
    const { isMobile } = useScreenSize();

    const filteredRows = removeHiddenRecords(rows);

    return (
        <TableContainer
            sx={{
                p: 1,
                borderRadius: "12px",
                border: "0.5px solid ",
                borderColor: theme.palette.senecSecondary.neutralGray20,
                backgroundColor: theme.palette.senecPrimary.white,
            }}
        >
            {isMobile ? (
                <EkpTableMobile
                    noDataText={noDataText}
                    rows={rows}
                    filteredRows={filteredRows}
                    loading={loading}
                    handleRowClick={handleRowClick}
                />
            ) : (
                <EkpTableDesktop
                    noDataText={noDataText}
                    rows={rows}
                    filteredRows={filteredRows}
                    loading={loading}
                    handleRowClick={handleRowClick}
                />
            )}
        </TableContainer>
    );
};

const removeHiddenRecords = (rows: RowData[]): RowData[] => {
    return rows.map((row) => {
        const filteredRow: RowData = {};
        for (const key in row) {
            if (!row[key].hidden) {
                filteredRow[key] = row[key];
            }
        }
        return filteredRow;
    });
};
